import api from './api';

export type UserFrameChannel = 'pro' | 'lite';

export interface UserFrame {
  _id: string;
  customFrameId: string;
  userId: string;
  name: string;
  description: string;
  frameImage: {
    url: string;
    path: string;
  };
  isActive: boolean;
  tags: string[];
  metadata: {
    originalFileName: string;
    fileSize: number;
    uploadDate: string;
  };
  customFrame: {
    _id: string;
    name: string;
    size: string;
    photoPositions: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GetUserFramesParams {
  page?: number;
  limit?: number;
  customFrameId?: string;
  tags?: string;
  isActive?: boolean;
  channel?: UserFrameChannel;
}

const apiBase = (channel: UserFrameChannel = 'pro') =>
  channel === 'lite' ? '/user-frames-lite' : '/user-frames';

export const userFrameService = {
  getUserFrames: async (params?: GetUserFramesParams) => {
    const channel = params?.channel || 'pro';
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.customFrameId) queryParams.append('customFrameId', params.customFrameId);
    if (params?.tags) queryParams.append('tags', params.tags);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const response = await api.get(`${apiBase(channel)}?${queryParams.toString()}`);
    return response.data;
  },

  getUserFrame: async (userFrameId: string, channel: UserFrameChannel = 'pro') => {
    const response = await api.get(`${apiBase(channel)}/${userFrameId}`);
    return response.data;
  },

  deleteUserFrame: async (userFrameId: string, channel: UserFrameChannel = 'pro') => {
    const response = await api.delete(`${apiBase(channel)}/${userFrameId}`);
    return response.data;
  },

  uploadUserFrames: async (customFrameId: string, files: File[], channel: UserFrameChannel = 'pro') => {
    const formData = new FormData();
    files.forEach((file) => formData.append('frameImages', file));
    formData.append('customFrameId', customFrameId);
    const response = await api.post(apiBase(channel), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getBackgroundsByCustomFrame: async (customFrameId: string, channel: UserFrameChannel = 'pro') => {
    const response = await api.get(`${apiBase(channel)}/custom-frame/${customFrameId}/backgrounds`);
    return response.data;
  },

  uploadBackgrounds: async (customFrameId: string, files: File[], channel: UserFrameChannel = 'pro') => {
    const formData = new FormData();
    files.forEach((file) => formData.append('backgroundImages', file));
    formData.append('customFrameId', customFrameId);
    const response = await api.post(`${apiBase(channel)}/backgrounds`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteBackground: async (backgroundId: string, channel: UserFrameChannel = 'pro') => {
    const response = await api.delete(`${apiBase(channel)}/backgrounds/${backgroundId}`);
    return response.data;
  },
};
